import React, { useCallback, useMemo, useRef } from 'react';
import { Alert, Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  MDXEditor,
  MDXEditorMethods,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  linkPlugin,
  linkDialogPlugin,
  imagePlugin,
  tablePlugin,
  codeBlockPlugin,
  codeMirrorPlugin,
  diffSourcePlugin,
  markdownShortcutPlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  CreateLink,
  InsertImage,
  InsertTable,
  InsertCodeBlock,
  InsertThematicBreak,
  ListsToggle,
  DiffSourceToggleWrapper,
  Separator,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { ProductExampleAttachmentDto } from 'apiClient/Api';
import { buildAttachmentRef, useAttachmentTokens } from 'Hooks/useAttachmentTokens';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  exampleId?: string | null;
  attachments?: ProductExampleAttachmentDto[];
  /** Uploads a file and returns the stored attachment — used by drag/drop and the toolbar upload button. */
  onUpload?: (file: File) => Promise<ProductExampleAttachmentDto | null>;
  placeholder?: string;
  disabled?: boolean;
}

const CODE_BLOCK_LANGUAGES = {
  '': 'Text',
  xml: 'XML',
  json: 'JSON',
  yaml: 'YAML',
  ini: 'INI',
  js: 'JavaScript',
  ts: 'TypeScript',
  bash: 'Bash',
  csharp: 'C#',
};

/**
 * WYSIWYG Markdown editor backed by @mdxeditor/editor. Uploads hook into the
 * product-example attachment pipeline: images dragged/dropped or pasted are
 * sent to `POST /ProductExample/{id}/attachments`, and the stored markdown
 * uses the `attachment:{id}` scheme (which the renderer resolves to a
 * short-lived token URL).
 */
export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  exampleId,
  attachments,
  onUpload,
  placeholder,
  disabled,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = useAttachmentTokens(exampleId ?? null);
  const editorRef = useRef<MDXEditorMethods>(null);

  // Keep the editor markdown in sync when the parent replaces the string
  // (e.g. after a save that re-hydrates form state from the server).
  React.useEffect(() => {
    const current = editorRef.current?.getMarkdown();
    if (current !== undefined && current !== value) {
      editorRef.current?.setMarkdown(value);
    }
  }, [value]);

  const attachmentIndex = useMemo(() => {
    const map = new Map<string, ProductExampleAttachmentDto>();
    (attachments ?? []).forEach((a) => { if (a.id) map.set(a.id, a); });
    return map;
  }, [attachments]);

  const imageUploadHandler = useCallback(async (file: File): Promise<string> => {
    if (!onUpload) throw new Error('upload handler missing');
    const att = await onUpload(file);
    if (!att?.id) throw new Error('upload failed');
    return buildAttachmentRef(att.id);
  }, [onUpload]);

  const imagePreviewHandler = useCallback(async (src: string): Promise<string> => {
    if (!src.startsWith('attachment:')) return src;
    const id = src.slice('attachment:'.length);
    if (!attachmentIndex.has(id)) return src;
    const resolved = await tokens.awaitToken(id);
    return resolved ?? src;
  }, [attachmentIndex, tokens]);

  const plugins = useMemo(() => [
    headingsPlugin(),
    listsPlugin(),
    quotePlugin(),
    thematicBreakPlugin(),
    linkPlugin(),
    linkDialogPlugin(),
    tablePlugin(),
    imagePlugin({ imageUploadHandler, imagePreviewHandler }),
    codeBlockPlugin({ defaultCodeBlockLanguage: '' }),
    codeMirrorPlugin({ codeBlockLanguages: CODE_BLOCK_LANGUAGES }),
    diffSourcePlugin({ viewMode: 'rich-text' }),
    markdownShortcutPlugin(),
    toolbarPlugin({
      toolbarContents: () => (
        <DiffSourceToggleWrapper>
          <UndoRedo/>
          <Separator/>
          <BoldItalicUnderlineToggles/>
          <Separator/>
          <BlockTypeSelect/>
          <Separator/>
          <ListsToggle/>
          <Separator/>
          <CreateLink/>
          <InsertImage/>
          <InsertTable/>
          <InsertThematicBreak/>
          <InsertCodeBlock/>
        </DiffSourceToggleWrapper>
      ),
    }),
  ], [imagePreviewHandler, imageUploadHandler]);

  if (!onUpload || !exampleId) {
    return (
      <Alert severity="info">{t('productExamples.attachments.needsDraft')}</Alert>
    );
  }

  return (
    <Box
      className={theme.palette.mode === 'dark' ? 'dark-theme dark-editor' : undefined}
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        '& .mdxeditor': {
          '--accentBase': theme.palette.primary.main,
          '--accentBgSubtle': theme.palette.action.hover,
          '--accentBgHover': theme.palette.action.selected,
          '--accentText': theme.palette.primary.main,
          '--accentTextContrast': theme.palette.primary.contrastText,
          '--baseBg': theme.palette.background.paper,
          '--baseText': theme.palette.text.primary,
          '--baseTextContrast': theme.palette.text.primary,
          bgcolor: 'background.paper',
          color: 'text.primary',
          minHeight: 420,
        },
        '& .mdxeditor-toolbar': {
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
          flexWrap: 'wrap',
        },
        '& .mdxeditor-root-contenteditable': {
          minHeight: 360,
          padding: 2,
        },
        '& .mdxeditor-root-contenteditable > div': {
          outline: 'none',
        },
      }}
    >
      <MDXEditor
        ref={editorRef}
        markdown={value}
        onChange={onChange}
        placeholder={placeholder ?? t('productExamples.edit.contentPlaceholder')}
        readOnly={disabled}
        plugins={plugins}
        contentEditableClassName="mdx-content"
      />
    </Box>
  );
};
